import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useFetchWithAuth } from '../session/useFetchWithAuth'
import toast from 'react-hot-toast'
import { useCurrentUser } from './useCurrentUser'
import { ProfileMatch } from './types'

const API_URL = import.meta.env.VITE_EPC_API_URL ?? ''

export const useFavoriteMatch = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()
  const { data: userData } = useCurrentUser()
  const userId = userData?.userId || ''

  return useMutation({
    mutationFn: async (targetProfileId: string) => {
      if (!userId) {
        throw new Error('User ID is required to toggle favorite status')
      }

      const response = await fetchWithAuth(
        `${API_URL}/profile/${userId}/favorite/${targetProfileId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      )
      return response
    },
    onMutate: async (targetProfileId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['profile', targetProfileId],
      })
      await queryClient.cancelQueries({
        queryKey: ['matchListData'],
      })

      // Snapshot the previous value
      const previousProfile = queryClient.getQueryData([
        'profile',
        targetProfileId,
      ])
      const previousMatchList = queryClient.getQueryData(['matchListData'])

      // Optimistically update profile data
      queryClient.setQueryData(['profile', targetProfileId], (old: any) => {
        if (!old) return old
        return { ...old, favorite: !old.favorite }
      })

      // Optimistically update matchList data
      queryClient.setQueryData(['matchListData'], (old: any) => {
        if (!old?.data) return old
        return {
          ...old,
          data: old.data.map((match: ProfileMatch) =>
            match.id === targetProfileId
              ? { ...match, favorite: !match.favorite }
              : match
          ),
        }
      })

      return { previousProfile, previousMatchList }
    },
    onSuccess: (data, targetProfileId) => {
      // Still invalidate to ensure consistency with the server
      queryClient.invalidateQueries({
        queryKey: ['profile', targetProfileId],
      })
      queryClient.invalidateQueries({
        queryKey: ['matchListData'],
      })
      toast.success(
        data?.isFavorite ? 'Added to favorites' : 'Removed from favorites'
      )
    },
    onError: (error, targetProfileId, context) => {
      // Revert optimistic updates on error
      if (context) {
        queryClient.setQueryData(
          ['profile', targetProfileId],
          context.previousProfile
        )
        queryClient.setQueryData(['matchListData'], context.previousMatchList)
      }
      console.error('Error toggling favorite status:', error)
      toast.error('Failed to update favorite status')
    },
  })
}
